import { CreatePageModule } from '@/connection/modules/create-page-module';
import { UpdateSession } from '@/connection/sessions/update-session';
import { useAppDispatch } from '@/store/hooks';
import { store } from '@/store/main';
import { saveWorkingProjectSessionByField } from '@/store/slices/project';
import { ModuleGroupingTypes, PageModuleType } from '@/types/module.types';
import { BrandliveEvent } from '@/types/projects.types';
import { Session } from '@/types/session.types';
import { Speaker } from '@/types/speakers.types';
import { Templates } from '@/types/template.types';
import { assertIsNumber } from '@/utils/assertions/assert-is-number';
import { PageModuleGroup } from '@/utils/modules/page-module-group-modules';

// extract the methods that create the speakers module and add it to both create and edit project
type TCreateSpeakers = {
  speakers: Speaker[];
  project?: BrandliveEvent | null;
  session: Session;
  dispatch: ReturnType<typeof useAppDispatch>;
  saveAsynchronously?: boolean;
};

const appendNewPageModulesToGroups = (uuid: string, newModules: number[]) => (group: PageModuleGroup) => {
  if (uuid === group.uuid) {
    return {
      ...group,
      modules: Array.from(new Set([...group.modules, ...newModules])),
    };
  }

  return group;
};

const CreateSpeakers = async ({
  speakers,
  project: _project,
  session,
  dispatch,
  saveAsynchronously,
}: TCreateSpeakers) => {
  const moduleGroups = session?.module_grouping;
  const overviewModuleGroup = moduleGroups?.find((grouping) => grouping.type === ModuleGroupingTypes.Overview);
  const sessionModules = session?.modules;

  let project = _project;
  if (!project) {
    const storeProj = store.getState()?.project?.workingProject;
    if (!storeProj) {
      throw new Error('Could not get working project to create speakers');
    }
    project = storeProj;
  }

  if (!moduleGroups || !overviewModuleGroup) {
    throw new Error('Failed to create speakers module');
  }

  let appendToGroup = false;

  let overviewSpeakers = sessionModules
    .filter((m) => m.type === PageModuleType.speakers)
    .find((g) => overviewModuleGroup.modules.includes(g.id ?? 0));

  if (!overviewSpeakers) {
    overviewSpeakers = await CreatePageModule({
      type: PageModuleType.speakers,
      eventName: project.name,
      template: Templates.Limelight,
      languages: session.languages,
      baseLanguage: session.default_language,
    });
    appendToGroup = true;
  }

  if (!overviewSpeakers) {
    throw new Error('Failed to create speakers module');
  }

  const overviewSpeakersId = overviewSpeakers.id;
  assertIsNumber(overviewSpeakersId);

  function addOverviewSpeakerModules() {
    if (!sessionModules || !overviewSpeakers) return [];
    // Ensure speakers are not added in duplicate
    const uniqueSpeakersMap = new Map(speakers.map((s) => [s.speaker, s]));
    const uniqueSpeakers = Array.from(uniqueSpeakersMap.values());
    const uniqueSpeakerIds = Array.from(uniqueSpeakersMap.keys());
    let modules = [...sessionModules];
    if (appendToGroup) {
      modules.push({
        ...overviewSpeakers,
        modules: uniqueSpeakers,
        content_modules: uniqueSpeakerIds,
        is_edited: true,
      });
    } else {
      modules = modules.map((m) => {
        if (m.id === overviewSpeakersId) {
          return {
            ...m,
            modules: uniqueSpeakers,
            content_modules: uniqueSpeakerIds,
            is_edited: true,
          };
        }
        return m;
      });
    }

    return modules;
  }

  const partialSessionUpdate: Partial<Session> & { uuid: string } = {
    uuid: session.uuid,
    modules: addOverviewSpeakerModules(),
    ...(appendToGroup
      ? {
          module_grouping: (moduleGroups as PageModuleGroup[]).map(
            appendNewPageModulesToGroups(overviewModuleGroup.uuid, [overviewSpeakersId])
          ),
        }
      : {}),
  };

  if (saveAsynchronously) {
    const updatedSession = await UpdateSession({
      ...session,
      ...partialSessionUpdate,
    });
    return updatedSession;
  } else {
    dispatch(saveWorkingProjectSessionByField(partialSessionUpdate));
  }
};

export default CreateSpeakers;
